import { encodePeriodValue } from '@components'

import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useGlobalFiscalYear } from '../../../../../../contexts/globalFiscalYearContext'
import { useLocalStorageUncontrolled } from '../../../../../../hooks/useLocalStorageUncontrolled'
import { useQueryParams } from '../../../../../../hooks/useQueryParams'
import { getPeriodValueFromFiscalYear } from '../../../../../../utils/getPeriodValueFromFiscalYear'
import { ReportsRoute } from '../../../../enums/reportsRoute'
import { PeriodComparisonValue } from '../../../../types/periodComparisonValue'
import { ReportOptions } from '../../../../types/reportOptions'
import { getLocalStorageFiltersValidated } from '../../../../utils/getLocalStorageFiltersValidated'
import { getReportsFiltersStorageKey } from '../../../../utils/getReportsFiltersStorageKey'

interface BalanceSheetReportOptionsUrl {
  compareWith: PeriodComparisonValue
  hideZeroes?: string
  compareCount?: string
  period?: string
  ytd?: string
}

interface BalanceSheetReportOptions extends ReportOptions {
  compareWith: PeriodComparisonValue
}

interface BalanceSheetFiltersContextState {
  appliedFilters: BalanceSheetReportOptions
  setQueryParams: (
    filters:
      | Partial<BalanceSheetReportOptionsUrl>
      | ((prevParams: Partial<BalanceSheetReportOptionsUrl>) => Partial<BalanceSheetReportOptionsUrl>),
  ) => void
}

const BalanceSheetFiltersContext = createContext<BalanceSheetFiltersContextState | undefined>(undefined)

const defaultReportsQuery: BalanceSheetReportOptionsUrl = {
  compareCount: '1',
  compareWith: 'year',
  hideZeroes: 'true',
  period: undefined,
  ytd: undefined,
}

interface BalanceSheetFiltersContextProviderProps {
  children: ReactNode
}

export const BalanceSheetFiltersContextProvider = ({ children }: BalanceSheetFiltersContextProviderProps) => {
  const { organization } = useUserOrganization()
  const balanceSheetStorageKey = getReportsFiltersStorageKey(ReportsRoute.Balance, organization?.id)

  const { setItem, getItem } = useLocalStorageUncontrolled(balanceSheetStorageKey)

  const { fiscalYear: globalFiscalYear } = useGlobalFiscalYear()
  const defaultPeriodValue =
    globalFiscalYear && organization?.id
      ? encodePeriodValue(getPeriodValueFromFiscalYear(globalFiscalYear), organization.id)
      : undefined

  const localStorageFilters = getLocalStorageFiltersValidated(defaultReportsQuery, getItem())

  const [queryParams, setQueryParams] = useQueryParams<BalanceSheetReportOptionsUrl>({
    defaultQuery: {
      ...defaultReportsQuery,
      ...(defaultPeriodValue ? { period: defaultPeriodValue } : {}),
      ...localStorageFilters,
    },
  })

  useEffect(() => {
    const localStorageFiltersString = JSON.stringify(localStorageFilters)
    const queryParamsString = JSON.stringify(queryParams)

    if (organization?.id && localStorageFiltersString !== queryParamsString) {
      setItem(queryParamsString)
    }
  }, [localStorageFilters, organization?.id, queryParams, setItem])

  const appliedFilters = useMemo(() => {
    const { hideZeroes, ytd, compareCount } = queryParams

    return {
      ...queryParams,
      hideZeroes: hideZeroes === 'true',
      ytd: ytd === 'true',
      compareCount: compareCount ? parseInt(compareCount, 10) : undefined,
    }
  }, [queryParams])

  return (
    <BalanceSheetFiltersContext.Provider
      value={{
        appliedFilters,
        setQueryParams,
      }}
    >
      {children}
    </BalanceSheetFiltersContext.Provider>
  )
}

export const useBalanceSheetFilters = () => {
  const context = useContext(BalanceSheetFiltersContext)

  if (!context) {
    throw new Error('BalanceSheetFiltersContext is missing in the module!')
  }

  return context
}
